import React, { memo, useMemo } from 'react';
import { cn } from '@/utils/cn';

export interface TypographyWithMentionProps {
  content: string;
  mentionClassName?: string;
  textClassName?: string;
  linkClassName?: string;
}

interface ParsedPart {
  text: string;
  isMention: boolean;
  isLink: boolean;
  key: string;
}

/**
 * Parses content string and extracts mentions in the format @name|id and URLs
 * @param content - The content string to parse
 * @returns Array of parsed parts with text, isMention flag, isLink flag, and unique key
 */

export const MENTION_PATTERN = /@([^\|@]+)\|([\w-]+)/g;
// URL pattern: matches http://, https://, or www. URLs, with optional trailing characters
const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

const parseMentionsAndLinks = (content: string): ParsedPart[] => {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const parts: ParsedPart[] = [];
  let partIndex = 0;

  // First, find all mentions and URLs with their positions
  const matches: Array<{ index: number; length: number; type: 'mention' | 'link'; text: string }> = [];

  // Find mentions
  const mentionPattern = MENTION_PATTERN;
  mentionPattern.lastIndex = 0;
  let mentionMatch;
  while ((mentionMatch = mentionPattern.exec(content)) !== null) {
    const mentionName = mentionMatch[1]?.trim();
    if (mentionName) {
      matches.push({
        index: mentionMatch.index,
        length: mentionMatch[0].length,
        type: 'mention',
        text: mentionName,
      });
    }
  }

  // Find URLs (excluding those that are part of mentions)
  const urlPattern = URL_PATTERN;
  urlPattern.lastIndex = 0;
  let urlMatch: RegExpExecArray | null;
  while ((urlMatch = urlPattern.exec(content)) !== null) {
    // Check if this URL is inside a mention
    const isInsideMention = matches.some(
      (m) => m.type === 'mention' && urlMatch!.index >= m.index && urlMatch!.index < m.index + m.length
    );
    if (!isInsideMention) {
      matches.push({
        index: urlMatch.index,
        length: urlMatch[0].length,
        type: 'link',
        text: urlMatch[0],
      });
    }
  }

  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);

  // Build parts array
  let lastIndex = 0;
  for (const match of matches) {
    // Add text before the match
    if (match.index > lastIndex) {
      const textBefore = content.substring(lastIndex, match.index);
      if (textBefore) {
        parts.push({
          text: textBefore,
          isMention: false,
          isLink: false,
          key: `text-${partIndex++}-${match.index}`,
        });
      }
    }

    // Add the match (mention or link)
    if (match.type === 'mention') {
      parts.push({
        text: match.text,
        isMention: true,
        isLink: false,
        key: `mention-${partIndex++}-${match.index}`,
      });
    } else {
      parts.push({
        text: match.text,
        isMention: false,
        isLink: true,
        key: `link-${partIndex++}-${match.index}`,
      });
    }

    lastIndex = match.index + match.length;
  }

  // Add remaining text after the last match
  if (lastIndex < content.length) {
    const remainingText = content.substring(lastIndex);
    if (remainingText) {
      parts.push({
        text: remainingText,
        isMention: false,
        isLink: false,
        key: `text-${partIndex}-${content.length}`,
      });
    }
  }

  return parts.length > 0 ? parts : [];
};

/**
 * TypographyWithMention component that parses and renders text with mentions and links.
 * Mentions are expected in the format: @name|id
 * Links are automatically detected and rendered as clickable links that open in a new tab.
 *
 * @example
 * <TypographyWithMention
 *   content="Hello @John Doe|user-123, check this https://example.com"
 *   mentionClassName="font-bold text-blue-500"
 * />
 */
const TypographyWithMention = memo<TypographyWithMentionProps>(
  ({ content, mentionClassName = 'font-bold', textClassName, linkClassName }: TypographyWithMentionProps) => {
    const parts = useMemo(() => parseMentionsAndLinks(content), [content]);

    // Handle empty or invalid content
    if (!content || typeof content !== 'string') {
      return null;
    }

    // If no mentions or links found, return plain content
    if (parts.length === 0) {
      return <>{content}</>;
    }

    const normalizeUrl = (url: string): string => {
      // If URL starts with www., add https://
      if (url.startsWith('www.')) {
        return `https://${url}`;
      }
      return url;
    };

    return (
      <>
        {parts.map((part) => {
          if (part.isMention) {
            return (
              <span key={part.key} className={cn('font-[700]', mentionClassName)}>
                @{part.text}
              </span>
            );
          } else if (part.isLink) {
            const url = normalizeUrl(part.text);
            return (
              <a
                key={part.key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn('underline hover:opacity-80 font-[700]', linkClassName)}
              >
                {part.text}
              </a>
            );
          } else {
            return (
              <span key={part.key} className={textClassName}>
                {part.text}
              </span>
            );
          }
        })}
      </>
    );
  }
);

TypographyWithMention.displayName = 'TypographyWithMention';

export default TypographyWithMention;
